import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Single address check, shared by the `email` field and the `email-tags` chips field.
 * Deliberately NOT trimming: the single-address field stores what the validator accepts, so
 * accepting ' info@klant.nl' would start saving padded addresses. Callers that split typed or
 * pasted text trim their tokens first.
 */
export function isValidEmailAddress(value: unknown): boolean {
  return typeof value === 'string' && EMAIL_PATTERN.test(value);
}

export function emailValidator(control: FormControl): ValidationErrors | null {
  if (!control.value) {
    // when there is no value, treat it as valid; use required for mandatory fields
    return null;
  }

  // This is also the globally registered 'email' validation (formly-root-config.ts), so an admin
  // can put it on the multi-address field too. Without this hand-off the joined array would be
  // matched as one string and any field holding two addresses would read as invalid.
  if (Array.isArray(control.value)) {
    return emailArrayValidator(control);
  }

  return isValidEmailAddress(control.value) ? null : { email: true };
}

/**
 * Array variant for the `email-tags` field. The component itself refuses an invalid address at
 * the input, so this guards the values that never pass through it: hydrated entry data, an
 * imported/mapped value, or a default value configured in the form builder.
 */
export function emailArrayValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value == null || value === '') {
    // no value = valid; use required for mandatory fields (same contract as emailValidator)
    return null;
  }

  // A non-array value is flagged rather than ignored. The field renders chips from a string it
  // finds (a type switched over from `email`, a mapped value) and normalises it on init, but a
  // write that lands afterwards would otherwise be invisible AND valid, and get saved back as is.
  if (!Array.isArray(value)) {
    return { email: true };
  }

  return value.every(isValidEmailAddress) ? null : { email: true };
}
